// RITA-1525 — Fabriken für das Vertragsdokument (Format v2). Reine Funktionen ohne DOM.
import { BISHERIGE_ZEILENHOEHE, ZEILENHOEHE_EINFACH } from './abstaende';
import type { VertragPlatzhalterKey } from './platzhalterkatalog';
import {
  Absatz,
  Block,
  Fusszeile,
  Inlineknoten,
  Kaestchenknoten,
  Kopfzeile,
  Liste,
  Platzhalterknoten,
  Schrifteinstellungen,
  Seiteneinstellungen,
  Seitenumbruch,
  Textlauf,
  Vertragsdokument,
  VERTRAGSDOKUMENT_FORMAT,
  VERTRAGSDOKUMENT_VERSION,
  Zeichenformat,
  Zeilenblock,
  Zeilenumbruchknoten,
} from './types';

export const STANDARD_SEITE: Seiteneinstellungen = {
  format: 'A4',
  raender: { obenMm: 15, rechtsMm: 20, untenMm: 12, linksMm: 20 },
  kopfHoeheMm: 25,
  fussHoeheMm: 15,
};

/** Schrift neuer Vorlagen: Zeilenabstand „einfach“, kein Abstand nach Absatz (RITA-1571, wie die Word-Muster). */
export const STANDARD_SCHRIFT: Schrifteinstellungen = {
  familie: 'Liberation Sans',
  groessePt: 11,
  zeilenhoehe: ZEILENHOEHE_EINFACH,
  absatzabstandEm: 0,
};

/** Schrift der Vorlagen von vor RITA-1571 (Zeilenhöhe 1.4, Abstand nach Absatz fehlt = 0.6 em). */
export const BISHERIGE_SCHRIFT: Schrifteinstellungen = {
  familie: 'Liberation Sans',
  groessePt: 11,
  zeilenhoehe: BISHERIGE_ZEILENHOEHE,
};

export const STANDARD_KOPF: Kopfzeile = {
  logo: { vertragsartId: null, position: 'rechts', hoeheMm: 12 },
  inhalt: [],
};

export const STANDARD_FUSS: Fusszeile = {
  inhalt: [],
  seitenzahl: { anzeigen: true, position: 'rechts' },
};

/**
 * Setzt nur die gesetzten Merkmale, in fester Reihenfolge (die Speicherform wird als Text verglichen), die eigene
 * Schriftgröße zuletzt (RITA-1534) – dieselbe Reihenfolge wie `formatBereinigen` im Server-Prüfmodul.
 */
function formatAnhaengen<T extends Zeichenformat>(knoten: T, format: Zeichenformat): T {
  const ergebnis: T = { ...knoten };
  if (format.fett) ergebnis.fett = true;
  if (format.kursiv) ergebnis.kursiv = true;
  if (format.unterstrichen) ergebnis.unterstrichen = true;
  if (format.groessePt !== undefined) ergebnis.groessePt = format.groessePt;
  return ergebnis;
}

export function text(inhalt: string, format: Zeichenformat = {}): Textlauf {
  return formatAnhaengen<Textlauf>({ typ: 'text', text: inhalt }, format);
}

export function platzhalter(schluessel: VertragPlatzhalterKey, format: Zeichenformat = {}): Platzhalterknoten {
  return formatAnhaengen<Platzhalterknoten>({ typ: 'platzhalter', schluessel }, format);
}

/** Textform eines Kästchens (Kopieren, abgeleiteter Text, HTML-Export); beim Einfügen wird daraus wieder ein Kästchen. */
export const KAESTCHEN_LEER = '\u2610';
export const KAESTCHEN_ANGEKREUZT = '\u2612';

export function kaestchen(angekreuzt = false): Kaestchenknoten {
  return { typ: 'kaestchen', angekreuzt };
}

export function zeilenumbruch(): Zeilenumbruchknoten {
  return { typ: 'zeilenumbruch' };
}

export function absatz(inhalt: Inlineknoten[] | string, id = ''): Absatz {
  return { typ: 'absatz', id, inhalt: typeof inhalt === 'string' ? [text(inhalt)] : inhalt };
}

export function liste(eintraege: Array<Inlineknoten[] | string>, id = ''): Liste {
  return {
    typ: 'liste',
    id,
    eintraege: eintraege.map((eintrag) => (typeof eintrag === 'string' ? [text(eintrag)] : eintrag)),
  };
}

export function seitenumbruch(id = ''): Seitenumbruch {
  return { typ: 'seitenumbruch', id };
}

/** Vergibt fehlende Block-Kennungen fortlaufend; vorhandene bleiben erhalten. */
export function mitBlockKennungen<T extends Block>(bloecke: T[], praefix = 'b'): T[] {
  const vergeben = new Set(bloecke.map((block) => block.id).filter((id) => id !== ''));
  let zaehler = 0;
  return bloecke.map((block) => {
    if (block.id !== '') return block;
    let id = '';
    do {
      zaehler += 1;
      id = `${praefix}${zaehler}`;
    } while (vergeben.has(id));
    vergeben.add(id);
    return { ...block, id };
  });
}

export interface Dokumentteile {
  seite?: Seiteneinstellungen;
  schrift?: Schrifteinstellungen;
  kopf?: Partial<Kopfzeile>;
  text?: Block[];
  fuss?: Partial<Fusszeile>;
}

/** Baut ein vollständiges Dokument mit Standardwerten und fortlaufenden Block-Kennungen. */
export function dokument(teile: Dokumentteile = {}): Vertragsdokument {
  const kopfInhalt = mitBlockKennungen<Zeilenblock>(teile.kopf?.inhalt ?? STANDARD_KOPF.inhalt, 'k');
  const fussInhalt = mitBlockKennungen<Zeilenblock>(teile.fuss?.inhalt ?? STANDARD_FUSS.inhalt, 'f');
  // Standardwerte werden kopiert, damit spätere Bearbeitung kein geteiltes Objekt verändert.
  const seite = teile.seite ?? STANDARD_SEITE;
  return {
    format: VERTRAGSDOKUMENT_FORMAT,
    version: VERTRAGSDOKUMENT_VERSION,
    seite: { ...seite, raender: { ...seite.raender } },
    schrift: { ...(teile.schrift ?? STANDARD_SCHRIFT) },
    kopf: { logo: { ...(teile.kopf?.logo ?? STANDARD_KOPF.logo) }, inhalt: kopfInhalt },
    text: mitBlockKennungen(teile.text ?? []),
    fuss: { inhalt: fussInhalt, seitenzahl: { ...(teile.fuss?.seitenzahl ?? STANDARD_FUSS.seitenzahl) } },
  };
}

export function leeresDokument(): Vertragsdokument {
  return dokument({ text: [absatz([])] });
}
